import { Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { useAuth } from './auth/AuthContext';
import { AppShell } from './components/AppShell';
import { CheckoutCancelPage } from './pages/CheckoutCancelPage';
import { CheckoutSuccessPage } from './pages/CheckoutSuccessPage';
import { CheckoutPage } from './pages/CheckoutPage';
import { AccountPage } from './pages/AccountPage';
import { CoachPage } from './pages/CoachPage';
import { DashboardPage } from './pages/DashboardPage';
import { DayReviewPage } from './pages/DayReviewPage';
import { LoginPage } from './pages/LoginPage';
import { NutritionPage } from './pages/NutritionPage';
import { OnboardingPage } from './pages/OnboardingPage';
import { ProfilePage } from './pages/ProfilePage';
import { MorePage } from './pages/MorePage';
import { ProgressPage } from './pages/ProgressPage';
import { SignupPage } from './pages/SignupPage';
import { SleepPage } from './pages/SleepPage';
import { WorkoutsPage } from './pages/WorkoutsPage';
import { WorkoutSessionPage } from './pages/WorkoutSessionPage';
import { WorkoutDetailPage } from './pages/WorkoutDetailPage';
import { WorkoutProgramPage } from './pages/WorkoutProgramPage';
import { WorkoutHistoryPage } from './pages/WorkoutHistoryPage';
import { CirclesPage } from './pages/CirclesPage';
import { CircleDetailPage } from './pages/CircleDetailPage';
import { CircleJoinPage } from './pages/CircleJoinPage';
import { LeaderboardsPage } from './pages/LeaderboardsPage';
import { PrivacyPage } from './pages/PrivacyPage';
import { TermsPage } from './pages/TermsPage';
import { LandingPage } from './pages/LandingPage';
import { DownloadPage } from './pages/DownloadPage';
import { ExecutivePage } from './pages/ExecutivePage';
import { isModuleEnabled } from './lib/trackingPrefs';
import type { TrackingModule } from './lib/trackingPrefs';
import { appPath } from './lib/appPaths';
import { isNativeApp } from './lib/platform';

function Splash() {
  return (
    <div className="app-body flex min-h-screen items-center justify-center text-sm text-zinc-500">
      Loading…
    </div>
  );
}

function RequireAuthedOnboarded() {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <Splash />;
  if (!user) {
    const next = encodeURIComponent(location.pathname + location.search);
    return <Navigate to={`/login?next=${next}`} replace />;
  }
  if (!user.onboardingCompleted) {
    return <Navigate to="/onboarding" replace />;
  }
  return <Outlet />;
}

function OnboardingGate() {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!user) return <Navigate to="/login" replace />;
  if (user.onboardingCompleted) {
    return <Navigate to={isNativeApp() ? '/' : user.aiCoachingEnabled ? '/account' : '/checkout'} replace />;
  }
  return <OnboardingPage />;
}

function RequireModule({ module }: { module: TrackingModule }) {
  const { user, loading } = useAuth();
  if (loading) return <Splash />;
  if (!isModuleEnabled(user, module)) return <Navigate to={appPath('/')} replace />;
  return <Outlet />;
}

function AuthedProductTree() {
  return (
    <>
      <Route element={<RequireModule module="workouts" />}>
        <Route path="workouts/session" element={<WorkoutSessionPage />} />
        <Route path="workouts/edit/:editId" element={<WorkoutSessionPage />} />
        <Route path="workouts/history/:workoutId" element={<WorkoutDetailPage />} />
      </Route>
      <Route element={<AppShell />}>
        <Route index element={<DashboardPage />} />
        <Route path="day-review" element={<DayReviewPage />} />
        <Route element={<RequireModule module="nutrition" />}>
          <Route path="nutrition" element={<NutritionPage />} />
        </Route>
        <Route element={<RequireModule module="sleep" />}>
          <Route path="sleep" element={<SleepPage />} />
        </Route>
        <Route element={<RequireModule module="workouts" />}>
          <Route path="workouts" element={<WorkoutsPage />} />
          <Route path="workouts/history" element={<WorkoutHistoryPage />} />
          <Route path="workouts/program" element={<WorkoutProgramPage />} />
        </Route>
        <Route element={<RequireModule module="goals" />}>
          <Route path="progress" element={<ProgressPage />} />
        </Route>
        <Route path="coach" element={<CoachPage />} />
        <Route path="circles" element={<CirclesPage />} />
        <Route path="circles/join/:inviteCode" element={<CircleJoinPage />} />
        <Route path="circles/:circleId" element={<CircleDetailPage />} />
        <Route path="leaderboards" element={<LeaderboardsPage />} />
        <Route path="more" element={<MorePage />} />
        <Route path="success" element={<CheckoutSuccessPage />} />
        <Route path="cancel" element={<CheckoutCancelPage />} />
        <Route path="profile" element={<ProfilePage />} />
        <Route path="*" element={<Navigate to={appPath('/')} replace />} />
      </Route>
    </>
  );
}

export default function App() {
  const { loading } = useAuth();

  if (isNativeApp()) {
    return (
      <Routes>
        <Route path="/login" element={loading ? <Splash /> : <LoginPage />} />
        <Route path="/signup" element={loading ? <Splash /> : <SignupPage />} />
        <Route path="/privacy" element={<PrivacyPage />} />
        <Route path="/terms" element={<TermsPage />} />
        <Route path="/onboarding" element={<OnboardingGate />} />
        <Route element={<RequireAuthedOnboarded />}>{AuthedProductTree()}</Route>
      </Routes>
    );
  }

  // Web funnel: landing → signup → onboarding → Stripe → App Store download
  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/login" element={loading ? <Splash /> : <LoginPage />} />
      <Route path="/signup" element={loading ? <Splash /> : <SignupPage />} />
      <Route path="/onboarding" element={loading ? <Splash /> : <OnboardingGate />} />
      <Route path="/checkout" element={loading ? <Splash /> : <CheckoutPage />} />
      <Route path="/account" element={loading ? <Splash /> : <AccountPage />} />
      <Route path="/download" element={loading ? <Splash /> : <DownloadPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/executive" element={loading ? <Splash /> : <ExecutivePage />} />
      <Route path="/success" element={<CheckoutSuccessPage />} />
      <Route path="/cancel" element={<CheckoutCancelPage />} />
      <Route path="/app/*" element={<Navigate to="/download" replace />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
